import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArticleVotesByArticleIdDocument,
  ArticlesForClusterDocument,
  usePlaceVoteMutation,
  useRemoveVoteMutation,
  VoteDetailFragmentDoc,
} from '../../../../../generated/graphql'
import Toast from 'react-native-root-toast'
import * as Sentry from '@sentry/react-native'
import { playUiSound } from '../../../../../utils/play-ui-sound.util'

const VOTES_PAGE_SIZE = 20

export type VoteChangePayload = { added: boolean }

type UseVoteActionsProps = {
  articleId: string
  /** Called once after a successful mutation. */
  onVoteChange?: (payload: VoteChangePayload) => void
}

export function useVoteActions({
  articleId,
  onVoteChange,
}: UseVoteActionsProps) {
  const { t } = useTranslation('perspective')

  const [doPlaceVote, { loading: placingVote, client: placeVoteClient }] =
    usePlaceVoteMutation({
      refetchQueries: [
        {
          query: ArticleVotesByArticleIdDocument,
          variables: {
            input: {
              articleId,
              limit: VOTES_PAGE_SIZE,
              offset: 0,
            },
          },
        },
      ],
      awaitRefetchQueries: false,
      update: (cache, result) => {
        if (result.data) {
          const { placeArticleVote } = result.data
          cache.modify({
            id: 'Perspective:' + articleId,
            fields: {
              articleVotes: () => {
                return placeArticleVote.map((v) =>
                  cache.writeFragment({
                    data: v,
                    fragment: VoteDetailFragmentDoc,
                  })
                )
              },
            },
          })
          cache.evict({ fieldName: 'articleVotesByArticleId' })
          cache.gc()
        }
      },
    })

  const [doRemoveVote, { loading: removingVote, client: removeVoteClient }] =
    useRemoveVoteMutation({
      refetchQueries: [
        {
          query: ArticleVotesByArticleIdDocument,
          variables: {
            input: {
              articleId,
              limit: VOTES_PAGE_SIZE,
              offset: 0,
            },
          },
        },
      ],
      awaitRefetchQueries: false,
      update: (cache, result) => {
        if (result.data) {
          const { removeArticleVote } = result.data
          cache.modify({
            id: 'Perspective:' + articleId,
            fields: {
              articleVotes: () => {
                return removeArticleVote.map((v) =>
                  cache.writeFragment({
                    data: v,
                    fragment: VoteDetailFragmentDoc,
                  })
                )
              },
            },
          })
          cache.evict({ fieldName: 'articleVotesByArticleId' })
          cache.gc()
        }
      },
    })

  const removeVote = useCallback(async () => {
    try {
      await doRemoveVote({
        variables: {
          input: {
            articleId,
          },
        },
      })
      Toast.show(t('Vote removed'))
      removeVoteClient.refetchQueries({
        include: [ArticlesForClusterDocument],
      })
      onVoteChange?.({ added: false })
    } catch (e) {
      Toast.show((e as Error).message)
      console.error(e)
      Sentry.captureException(e)
    }
  }, [articleId, doRemoveVote, removeVoteClient, onVoteChange, t])

  const placeVote = useCallback(
    async (justification: string) => {
      try {
        await doPlaceVote({
          variables: {
            input: {
              articleId,
              justification: justification.trim().length
                ? justification.trim()
                : null,
            },
          },
        })
        void playUiSound('vote')
        onVoteChange?.({ added: true })
        placeVoteClient.refetchQueries({
          include: [ArticlesForClusterDocument],
        })
        Toast.show(t('Vote placed'))
      } catch (e) {
        Toast.show((e as Error).message)
        console.error(e)
        Sentry.captureException(e)
      }
    },
    [articleId, doPlaceVote, placeVoteClient, onVoteChange, t]
  )

  return {
    placeVote,
    removeVote,
    placingVote,
    removingVote,
    loading: placingVote || removingVote,
  }
}
